
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type CsmMomentsBlendShaderUniforms = {
	uPrev: WebGLUniformLocation,
	uFresh: WebGLUniformLocation,
	uCurrDepth: WebGLUniformLocation,
	uInvCurrLightVP: WebGLUniformLocation,
	uPrevLightVP: WebGLUniformLocation,
	uAlpha: WebGLUniformLocation
};

export type CsmMomentsBlendShaderAttributes = {
	aVertexPosition: number,
	vTexCoord: number
};

export type CsmMomentsBlendShaderSettings = string[];

export class CsmMomentsBlendShader extends ShaderBase<CsmMomentsBlendShaderUniforms, CsmMomentsBlendShaderAttributes> {

    private static fragmentShaderSource = `
in vec2 vTexCoord;
out vec4 fragColor;

uniform sampler2D uPrev;
uniform sampler2D uFresh;
uniform sampler2D uCurrDepth;
uniform mat4 uInvCurrLightVP;
uniform mat4 uPrevLightVP;
uniform float uAlpha;

// Must match csm_shadow.glsl and csm-moments-resolve-fragment.glsl.
#define C_EVSM 5.0

// Temporal reprojection of EVSM moments. Each output texel of the new
// momentsTextures[i] is at world-space position P, reconstructed from
// (vTexCoord, currentDepth) via the inverse of the cascade's *current*
// light VP. We then project P through the *previous* cascade VP to find
// the matching texel in \`uPrev\`, and EMA-blend it with the freshly-blurred
// moments at this texel.
//
// Without reprojection, blending \`prev[uv]\` with \`fresh[uv]\` mixes moments
// for two different world points whenever the cascade matrix changes
// (sun rotation, camera-driven snap shift, cache rebuild). That produced
// the "shades of shadow at randomized offsets" wobble — the smoothing was
// happening, but on mismatched data. Reprojection ensures \`prev\` is
// sampled at the world-coincident texel, so the EMA accumulates a
// consistent signal across matrix changes.
//
// Falls back to fresh-only when the reprojected UV lands outside [0,1]
// (the new matrix uncovers area the prev matrix didn't see), avoiding
// black borders or wraparound from an out-of-range sample.
//
// Caveat: depth read from the unblurred cascade depth texture is the
// nearest caster's depth at this shadow-map texel; for blurred-moments
// regions near silhouettes the reconstructed world point is approximate
// (it sits on whatever caster the depth pass recorded, not on the per-blur
// kernel-average surface). In practice this is the same approximation TAA
// makes when reprojecting through motion vectors and is good enough for
// near-stable signals.
void main(void) {
	vec2 fresh = texture(uFresh, vTexCoord).rg;

	float d = texture(uCurrDepth, vTexCoord).r;
	vec3 ndcCurr = vec3(vTexCoord * 2.0 - 1.0, d * 2.0 - 1.0);
	vec4 worldH = uInvCurrLightVP * vec4(ndcCurr, 1.0);
	vec3 world = worldH.xyz / worldH.w;

	vec4 prevClip = uPrevLightVP * vec4(world, 1.0);
	vec3 prevNdc = prevClip.xyz / prevClip.w;
	vec2 uvPrev = prevNdc.xy * 0.5 + 0.5;

	if (uvPrev.x < 0.0 || uvPrev.x > 1.0 || uvPrev.y < 0.0 || uvPrev.y > 1.0) {
		fragColor = vec4(fresh, 0.0, 0.0);
		return;
	}

	vec2 prev = texture(uPrev, uvPrev).rg;

	// Re-warp prev moments into current's depth axis. EVSM stores moments as
	// exponentials of light-space window depth (\`exp(c·d)\`). When the cascade
	// matrix changes between frames (sun rotation, camera-snap shift, cache
	// rebuild), the same world point P projects to *different* depths under
	// prev vs current — \`prev.x\` measures \`exp(c·d_prev_of_P)\` while \`fresh.x\`
	// measures \`exp(c·d_curr_of_P)\`. Naively mixing them produces a fictional
	// in-between depth that takes several EMA frames to converge — visible as
	// the "intensity grows darker then brighter and settles" wobble after
	// camera motion.
	//
	// The depth delta for THIS texel's world point is \`d - dPrev\`, where d
	// (current depth at vTexCoord) we already have, and dPrev is the NDC z of
	// P projected through the prev matrix (already computed). Multiplying
	// prev.x by \`exp(c · delta)\` shifts its exponential into current's depth
	// space; prev.y is M2 = M1², so it shifts by the square. Holds exactly
	// for a single-caster pixel; degrades gracefully near silhouettes where
	// the blur kernel mixes multiple surfaces with different deltas.
	float dPrev = prevNdc.z * 0.5 + 0.5;
	float k = exp(C_EVSM * (d - dPrev));
	prev.x *= k;
	prev.y *= k * k;

	// Silhouette / disocclusion guard. The single-sample depth correction
	// above is exact for one continuous surface but wrong where the blur
	// kernel mixes casters at different depths — each surface has its own
	// (d_curr − d_prev) but we shifted the whole kernel by the receiver's
	// delta. As the sun rotates that mismatch shows up as edge shimmer.
	//
	// The fresh moments' own variance (\`M2 - M1²\`) is a direct silhouette
	// detector: zero in flat regions where the kernel covers a single
	// caster depth, large where the kernel straddles a depth jump. Use
	// it to bias alpha toward 1.0 (fresh-only) at silhouettes; flat
	// regions keep the smooth EMA. Normalized by \`M1²\` so the threshold
	// stays scale-invariant across the EVSM range.
	//
	// 0.01 → 0.3 thresholds: flat-region noise (per-frame depth-pass
	// jitter from sub-texel snapping) sits below 0.01; the 50/50 caster
	// mix at a sharp shadow edge tops out near 0.6 in normalized
	// variance, so 0.3 saturates the guard at clearly-mixed pixels.
	float variance = max(fresh.y - fresh.x * fresh.x, 0.0);
	float varianceNorm = variance / max(fresh.x * fresh.x, 1e-6);
	float effectiveAlpha = mix(uAlpha, 1.0, smoothstep(0.01, 0.3, varianceNorm));
	fragColor = vec4(mix(prev, fresh, effectiveAlpha), 0.0, 0.0);
}
`;

    private static vertexShaderSource = `
// --- begin: fullscreen_quad.vert.glsl ---
layout(location = 0) in vec2 aVertexPosition;
out vec2 vTexCoord;

void main(void) {
	vTexCoord = aVertexPosition * 0.5 + 0.5;
	gl_Position = vec4(aVertexPosition, 0.0, 1.0);
}
// --- end: fullscreen_quad.vert.glsl ---
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: CsmMomentsBlendShaderUniformsContainer;
    public attributesContainer: CsmMomentsBlendShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: CsmMomentsBlendShaderSettings) {
        super(ctx, CsmMomentsBlendShader.fragmentShaderSource, CsmMomentsBlendShader.vertexShaderSource, settings);
        this.uniformsContainer = new CsmMomentsBlendShaderUniformsContainer(ctx, this);
        this.attributesContainer = new CsmMomentsBlendShaderShaderAttributesContainer(ctx, this);
    }
}

class CsmMomentsBlendShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: CsmMomentsBlendShader) {
        super(ctx);
    }

    public setPrev(texture: Texture) : CsmMomentsBlendShaderUniformsContainer {
        this.setTextureValue('uPrev', this.shader.uniforms.uPrev, texture, 'sampler2D');
        return this;
    }

    public setFresh(texture: Texture) : CsmMomentsBlendShaderUniformsContainer {
        this.setTextureValue('uFresh', this.shader.uniforms.uFresh, texture, 'sampler2D');
        return this;
    }

    public setCurrDepth(texture: Texture) : CsmMomentsBlendShaderUniformsContainer {
        this.setTextureValue('uCurrDepth', this.shader.uniforms.uCurrDepth, texture, 'sampler2D');
        return this;
    }

    public setInvCurrLightVP(value: mat4 | number[]) : CsmMomentsBlendShaderUniformsContainer {
        this.setValue('uInvCurrLightVP', this.shader.uniforms.uInvCurrLightVP, value, 'mat4');
        return this;
    }

    public setPrevLightVP(value: mat4 | number[]) : CsmMomentsBlendShaderUniformsContainer {
        this.setValue('uPrevLightVP', this.shader.uniforms.uPrevLightVP, value, 'mat4');
        return this;
    }

    public setAlpha(value: number) : CsmMomentsBlendShaderUniformsContainer {
        this.setValue('uAlpha', this.shader.uniforms.uAlpha, value, 'float');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uPrev', uniform: this.shader.uniforms.uPrev, dataType: 'sampler2D' },
			{ name: 'uFresh', uniform: this.shader.uniforms.uFresh, dataType: 'sampler2D' },
			{ name: 'uCurrDepth', uniform: this.shader.uniforms.uCurrDepth, dataType: 'sampler2D' }
        ];
    }
}

class CsmMomentsBlendShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: CsmMomentsBlendShader) {
        super(ctx);
    }

    public setVertexPosition(settings: { length: number, offset: number, itemSize: number }) : CsmMomentsBlendShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvTexCoord(settings: { length: number, offset: number, itemSize: number }) : CsmMomentsBlendShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vTexCoord, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}